import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Clock } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { api, eur } from "@/lib/api";
import { ExVat, Totals } from "@/components/shop/Totals";

export default function OrderSuccess() {
  const { id } = useParams();
  const { t } = useLang();
  const [o, setO] = useState(null);

  const confirmed = useRef(false);
  useEffect(() => {
    let n = 0;
    const load = () => api.get(`/orders/${id}`).then((r) => {
      setO(r.data);
      if (r.data.status === "pending" && ++n < 20) timer = setTimeout(load, 3000);
    }).catch(() => setO(false));
    let timer;
    const q = new URLSearchParams(window.location.search);
    if (q.get("data") && q.get("ss1") && !confirmed.current) {
      confirmed.current = true;
      api.post("/paysera/confirm", { data: q.get("data"), ss1: q.get("ss1") }).catch(() => {}).finally(() => {
        window.history.replaceState(null, "", window.location.pathname);
        load();
      });
    } else load();
    return () => clearTimeout(timer);
  }, [id]);

  if (!o) return <div className="h-96" />;
  const paid = o.status !== "pending";
  const cod = o.payment_method === "cod";

  return (
    <div data-testid="order-success-page" className="mx-auto max-w-2xl px-4 sm:px-6 py-20 rise">
      {paid ? <CheckCircle2 className="w-14 h-14 text-emerald-400" /> : <Clock className="w-14 h-14 text-amber-400" />}
      <h1 data-testid="order-status-title" className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-6">
        {cod ? t("cod_title") : paid ? t("success_title") : t("pending")}
      </h1>
      {paid ? <p className="text-slate-400 mt-4">{t(cod ? "cod_sub" : "success_sub")}</p> : <p data-testid="order-confirming" className="text-slate-400 mt-4">{t("payment_confirming")} <Link to={`/pay/${id}`} className="text-orange-500 underline underline-offset-2">{t("pay_again")}</Link></p>}
      <dl className="mt-10 border border-[#3B4157] divide-y divide-[#3B4157] text-sm">
        <div className="flex justify-between px-4 py-3"><dt className="text-slate-400">{t("order_no")}</dt><dd data-testid="order-number" className="font-mono-x">{o.number}</dd></div>
        {o.transaction_id && <div className="flex justify-between px-4 py-3"><dt className="text-slate-400">{t("txn")}</dt><dd data-testid="order-txn" className="font-mono-x">{o.transaction_id}</dd></div>}
        {o.items.map((i) => (
          <div key={i.product_id} className="flex justify-between px-4 py-3"><dt>{i.quantity} × {i.name}{i.options?.length > 0 && <div className="text-xs text-slate-500">+ {i.options.join(", ")}</div>}</dt><dd className="font-mono-x whitespace-nowrap">{eur(i.price * i.quantity)}{o.subtotal != null && <ExVat />}</dd></div>
        ))}
        {o.subtotal != null ? <div className="px-4 py-3"><Totals net={o.subtotal} vat={o.vat} total={o.total} shipping={o.shipping} testid="order-total" big="text-2xl" />{cod && <div data-testid="order-cod-due" className="text-xs text-amber-400 mt-2 text-right">{t("cod_pay_amount")}: {eur(o.total)}</div>}</div>
          : <div className="flex justify-between px-4 py-3"><dt className="uppercase tracking-widest text-slate-400">{t("total")}</dt><dd data-testid="order-total" className="font-display font-black text-2xl text-orange-500">{eur(o.total)}</dd></div>}
      </dl>
      <Link to="/" data-testid="order-back-shop-link" className="inline-block mt-10 text-orange-500 uppercase tracking-widest font-display font-semibold">{t("back_shop")} →</Link>
    </div>
  );
}
